import React from 'react';
import { Link } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import SEO, { SITE_URL } from './SEO';
import Footer from './Footer';
import CalculatorEnhanced from './CalculatorEnhanced';
import './PricingPage.css';
import './RateCard.css';

// PRIVATE rate card. Not indexed, not in the sitemap, not linked from the site —
// reachable only by direct URL, for sales conversations.
const PACKAGES = [
  {
    name: '24/7 Call Coverage',
    monthly: '$500',
    setup: '$750',
    minutes: '500 minutes included',
    bullets: [
      'Every call answered, 24/7/365',
      'Your FAQs: hours, location, services, pricing ranges',
      'Message taking with name, number, and intent',
      'Call summaries after every call',
      'Live transfers to your team',
      'Email + SMS notifications',
    ],
  },
  {
    name: 'AI Front Desk',
    monthly: '$899',
    setup: '$1,500',
    minutes: '1,500 minutes included',
    featured: true,
    bullets: [
      'Everything in 24/7 Call Coverage',
      'Appointment booking into your calendar',
      'Lead qualification with your screening questions',
      'Bilingual English/Spanish call flows',
      'One calendar or CRM integration',
    ],
  },
  {
    name: 'Complete Intake Team',
    monthly: '$1,799',
    setup: '$2,500',
    minutes: '3,500 minutes included',
    bullets: [
      'Everything in AI Front Desk',
      'Phone + email + web chat coverage',
      'Advanced intake workflows',
      'Two integrations',
      'Routing and escalation rules',
      'Follow-up and reporting',
    ],
  },
  {
    name: 'Enterprise Operations',
    monthly: '$2,750',
    from: true,
    setup: 'Custom',
    minutes: 'Volume allowance, scoped',
    bullets: [
      'Multiple locations and numbers',
      'Custom workflows',
      'Outbound automation',
      'Additional integrations',
      'Priority support',
    ],
  },
];

const COMPARE = [
  ['', '24/7 Call Coverage', 'AI Front Desk', 'Complete Intake'],
  ['Monthly', '$500', '$899', '$1,799'],
  ['Setup (waived on 12-mo)', '$750', '$1,500', '$2,500'],
  ['Minutes included', '500', '1,500', '3,500'],
  ['Overage beyond allowance', '~$0.35/min', '~$0.30/min', '~$0.25/min'],
  ['Answering + FAQs + messages', 'Yes', 'Yes', 'Yes'],
  ['Appointment booking', '—', 'Yes', 'Yes'],
  ['Lead qualification', '—', 'Yes', 'Yes'],
  ['Bilingual EN/ES', '—', 'Yes', 'Yes'],
  ['Email + web chat', '—', '—', 'Yes'],
  ['Integrations', '—', '1', '2'],
  ['Reporting', 'Call summaries', 'Call summaries', 'Full reporting'],
];

const ADDONS = [
  ['Email Agent', '$500', '$299/mo', 'Inbox classification, draft responses, FAQs, follow-up and escalation. Approved categories can send automatically.'],
  ['Web Chat Agent', '$300', '$199/mo', 'Website questions, lead capture, qualification, and appointment booking.'],
  ['CRM / Case System Integration', '$500–$1,500', '$150–$400/mo', 'Sync calls, leads, notes, statuses, and appointments.'],
  ['Outbound Follow-Up', '$750', '$699/mo', 'Follow-up to existing leads by email/SMS, sequences, replies, and appointment booking.'],
  ['Managed Cold Outreach', '$1,500', 'From $1,250/mo', 'Lead list, campaign setup, email infrastructure, personalization, reply qualification, and reporting.'],
  ['Conversion Website', '$1,500–$4,000', '$99–$249/mo', 'Website or landing pages, hosting, updates, analytics, and agent integration.'],
  ['Additional Phone Minutes', '—', 'Usage-based', 'Volume beyond your included allowance, at your plan’s published rate.'],
  ['Additional Location', '$500', '$299–$699/mo', 'Separate number, knowledge base, routing, and reporting.'],
];

const VERTICALS = [
  {
    name: 'Property Management Front Desk',
    price: '$1,499–$2,499',
    bullets: [
      'Tenant, owner, vendor, and prospect identification',
      'Maintenance-request intake',
      'Emergency escalation on strict rules',
      'Showing and leasing appointment scheduling',
      'Vendor routing',
      'Call, email, and text follow-up',
      'Property-management software integration where supported',
      '24/7 and bilingual coverage',
    ],
  },
  {
    name: 'Law Firm Intake Team',
    price: '$1,499–$2,999',
    bullets: [
      'New-matter intake',
      'Practice-area qualification',
      'Consultation scheduling',
      'Urgency and after-hours routing',
      'Bilingual coverage',
      'CRM / case-management notes',
      'Email and text follow-up',
      'Required disclosures and human escalation',
    ],
  },
];

function PlansPage() {
  return (
    <>
      <SEO
        title="Plans & Rate Card | UBOTIKA"
        description="UBOTIKA package pricing, add-ons, and vertical bundles."
        path="/plans"
        noindex
      />
      <main id="main-content" className="pricing-page ratecard-page">
        <section className="pricing-hero">
          <span className="ratecard-note">Private rate card · not listed publicly</span>
          <h1>Choose the coverage your business needs.</h1>
          <p className="pricing-sub">
            Every package handles the routine front-desk and intake workload 24/7 for a
            fraction of the cost of expanding human coverage. Your monthly price is fixed
            and includes a minute allowance — you are never metered from the first minute
            the way an answering service bills you.
          </p>
        </section>

        <section className="ratecard-section">
          <div className="ratecard-grid ratecard-grid--4">
            {PACKAGES.map((p) => (
              <article key={p.name} className={`ratecard-card${p.featured ? ' featured' : ''}`}>
                {p.featured && <span className="ratecard-flag">Most chosen</span>}
                <h3>{p.name}</h3>
                <div className="ratecard-price">
                  {p.from && <span className="ratecard-from">From</span>}
                  {p.monthly}
                  <span>/month</span>
                </div>
                <p className="ratecard-setup">
                  Setup <strong>{p.setup}</strong>
                  {p.setup !== 'Custom' && ' — waived on a 12-month agreement'}
                </p>
                <div className="ratecard-minutes">{p.minutes}</div>
                <ul className="ratecard-list">
                  {p.bullets.map((b) => (
                    <li key={b}><FaCheck /><span>{b}</span></li>
                  ))}
                </ul>
                <Link to="/free-missed-lead-audit" className="cta-button">
                  Start with a free audit
                </Link>
              </article>
            ))}
          </div>
          <p className="ratecard-fine">
            Minutes are a fair-use allowance, not a meter. If you consistently run past your
            allowance we move you to the right package rather than surprise you on an invoice.
            Overage rates, if they ever apply, are published above and agreed before you sign.
          </p>
        </section>

        <section className="ratecard-section">
          <h2>How the packages compare</h2>
          <div className="ratecard-table-wrap">
            <table className="ratecard-table">
              <thead>
                <tr>{COMPARE[0].map((h) => <th key={h || 'x'}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {COMPARE.slice(1).map((row) => (
                  <tr key={row[0]}>
                    <td>{row[0]}</td>
                    {row.slice(1).map((cell, i) => (
                      <td key={i} className={/^\$|min$|^\d/.test(cell) ? 'num' : undefined}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="ratecard-section">
          <h2>Built for your industry</h2>
          <p className="ratecard-lede">
            Two bundles we run most often. Both assume the AI handles the routine majority and
            escalates anything sensitive or unusual to your team.
          </p>
          <div className="ratecard-grid">
            {VERTICALS.map((v) => (
              <article key={v.name} className="ratecard-vertical">
                <h3>{v.name}</h3>
                <div className="ratecard-price">
                  {v.price}
                  <span>/month</span>
                </div>
                <ul className="ratecard-list">
                  {v.bullets.map((b) => (
                    <li key={b}><FaCheck /><span>{b}</span></li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="ratecard-section">
          <h2>Add-ons</h2>
          <p className="ratecard-lede">
            Stack any of these onto a package. Cold outbound calling is deliberately not offered
            until consent, dialing, and compliance rules are fully defined.
          </p>
          <div className="ratecard-table-wrap">
            <table className="ratecard-table">
              <thead>
                <tr><th>Add-on</th><th>Setup</th><th>Monthly</th><th>Included</th></tr>
              </thead>
              <tbody>
                {ADDONS.map((row) => (
                  <tr key={row[0]}>
                    <td>{row[0]}</td>
                    <td className="num">{row[1]}</td>
                    <td className="num">{row[2]}</td>
                    <td>{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <CalculatorEnhanced />

        <section className="ratecard-pilot">
          <h2>Not ready to commit? Try it free for two weeks.</h2>
          <p>
            We build your agent, point your calls at it, and you listen to real recordings
            of your own business being answered. Your first two weeks are free. If you
            continue, the Starter plan is $500/month — not $500 for the trial. Higher-tier
            plans and any setup fees are confirmed in your quote.
          </p>
          <Link to="/free-missed-lead-audit" className="cta-button">Start the pilot</Link>
        </section>

        <section className="ratecard-section">
          <p className="ratecard-fine">
            What the AI does not do: it does not handle every angry tenant, sensitive legal
            matter, or unusual situation. It answers the routine majority correctly, 24/7, and
            escalates the rest to your team on rules you set. Prefer to hear it first? Call{' '}
            <a href="tel:+14244344541">(424) 434-4541</a> — an AI picks up.
          </p>
        </section>
        <Footer />
      </main>
    </>
  );
}

export default PlansPage;
